import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";

// Column widths of the planner table (Category … Actions), so rows line up.
const COLS = ["25%", "12%", "9%", "13%", "14%", "12%", "8%", "7%"];

/**
 * Loading placeholder for the demand planner, shaped like the page under the
 * top bar: the 6 budget cards, the allocation bar, the category filter tabs
 * and the category table.
 */
export default function PlannerSkeleton({ darkMode, rows = 8 }) {
  const paperBg = darkMode ? "#1a1a1a" : "#fff";
  const border = darkMode ? "#333" : "#e8e8e8";
  return (
    <Box aria-busy="true" aria-label="Loading the plan">
      {/* Budget summary cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(6, minmax(0, 1fr))" },
          gap: 1.5,
          mb: 2,
        }}
      >
        {Array.from({ length: 6 }, (_, i) => (
          <Paper key={i} sx={{ p: 1.5, bgcolor: paperBg }}>
            <Skeleton variant="text" width="55%" height={16} />
            <Skeleton variant="text" width="75%" height={32} />
            <Skeleton variant="text" width="45%" height={14} />
          </Paper>
        ))}
      </Box>

      {/* Allocation bar */}
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
          <Skeleton variant="text" width={160} height={16} />
          <Skeleton variant="text" width={110} height={16} />
        </Box>
      </Box>

      <Paper sx={{ bgcolor: paperBg, overflow: "hidden" }}>
        {/* Filter tabs */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 1.5, borderBottom: `1px solid ${border}` }}>
          {[130, 52, 70, 64, 56, 58, 72, 60, 80].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={28} sx={{ borderRadius: 4 }} />
          ))}
        </Box>

        {/* Table header + category rows */}
        <Box sx={{ display: "flex", px: 1.5, py: 1.25, gap: 1.5, borderBottom: `1px solid ${border}` }}>
          {COLS.map((w, i) => (
            <Box key={i} sx={{ width: w }}>
              <Skeleton variant="text" width="60%" height={18} />
            </Box>
          ))}
        </Box>
        {Array.from({ length: rows }, (_, r) => (
          <Box
            key={r}
            sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1.25, gap: 1.5, borderBottom: `1px solid ${border}` }}
          >
            <Box sx={{ width: COLS[0] }}>
              <Skeleton variant="text" width={`${55 + ((r * 13) % 30)}%`} height={20} />
            </Box>
            <Box sx={{ width: COLS[1] }}>
              <Skeleton variant="rounded" width="80%" height={18} />
            </Box>
            {COLS.slice(2, 6).map((w, i) => (
              <Box key={i} sx={{ width: w, display: "flex", justifyContent: "flex-end" }}>
                <Skeleton variant="text" width="65%" height={20} />
              </Box>
            ))}
            <Box sx={{ width: COLS[6], display: "flex", justifyContent: "center" }}>
              <Skeleton variant="rounded" width={48} height={18} />
            </Box>
            <Box sx={{ width: COLS[7], display: "flex", justifyContent: "center" }}>
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
